import { SITE_URL } from './seo'

export interface FaqItem {
  question: string
  answer: string
}

export interface BreadcrumbItem {
  name: string
  url: string
}

export function organizationSchema(): Record<string, unknown> {
  return {
    '@type': 'Organization',
    name: 'CountAct',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/full-logo.webp`,
      width: 1920,
      height: 245,
    },
    description:
      'CountAct aide les établissements à piloter leur sûreté, leur conformité et leurs obligations réglementaires.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '24 Rue Raspail',
      postalCode: '38000',
      addressLocality: 'Grenoble',
      addressCountry: 'FR',
    },
    telephone: '+33642254626',
    email: 'countact@countact.fr',
    sameAs: ['https://www.linkedin.com/company/countact/'],
  }
}

export function websiteSchema(): Record<string, unknown> {
  return { '@type': 'WebSite', name: 'CountAct', url: SITE_URL }
}

export function homeUrl(locale: string): string {
  return `${SITE_URL}/${locale}`
}

export function homeBreadcrumbItem(locale: string): BreadcrumbItem {
  return { name: locale === 'en' ? 'Home' : 'Accueil', url: homeUrl(locale) }
}

export function breadcrumbSchema(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

// Returns undefined when there are no items so callers can skip emitting an
// empty FAQPage.
export function faqSchema(items?: FaqItem[]): Record<string, unknown> | undefined {
  if (!items?.length) return undefined
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

export interface ArticleSchemaInput {
  title: string
  description: string
  datePublished: string
  dateModified: string
  authorName?: string
  url: string
  image: string
}

export function articleSchema(input: ArticleSchemaInput): Record<string, unknown> {
  return {
    '@type': 'Article',
    headline: input.title,
    description: input.description,
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: input.authorName ? { '@type': 'Person', name: input.authorName } : undefined,
    publisher: organizationSchema(),
    image: input.image,
    mainEntityOfPage: input.url,
  }
}

export interface ServiceSchemaInput {
  name: string
  description: string
  url: string
}

export function serviceSchema(input: ServiceSchemaInput): Record<string, unknown> {
  return {
    '@type': 'Service',
    name: input.name,
    description: input.description,
    url: input.url,
    provider: organizationSchema(),
  }
}

export interface SoftwareApplicationSchemaInput {
  name: string
  description: string
  url: string
}

export function softwareApplicationSchema(input: SoftwareApplicationSchemaInput): Record<string, unknown> {
  return {
    '@type': 'SoftwareApplication',
    name: input.name,
    description: input.description,
    url: input.url,
    applicationCategory: 'BusinessApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    provider: organizationSchema(),
  }
}
